import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { AreaDetail, MonitoringDetail, MonitoringListItem, MonitoringSettings } from '@tww/shared';
import { GROWTH_BEHAVIOR_LABELS, fmtDate, todayIso, windowLabel } from '@tww/shared';
import type { EmailDto } from '@tww/shared';
import { EmailModal } from '../emails/EmailModal';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { Panel, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { DateInput, Select, TextArea } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { DuePill, YEAR_OPTIONS, conditionLabel, improvementLabel, monitoringTitle, useUsersPick, wildlifeLabel } from './common';

/** Panel „Monitoring“ in der Flächenakte (Plan 05 Abschnitt 5.2): Historie, Regelhinweis, Fußzeile mit nächstem Termin und Aktionen */
export function AreaMonitoringPanel({ area }: { area: AreaDetail }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const write = can('monitoring', 'write');
  const q = useQuery({ queryKey: ['area-monitorings', area.id], queryFn: () => api.get<{ items: MonitoringListItem[]; settings: MonitoringSettings }>(`/areas/${area.id}/monitorings`), enabled: can('monitoring') });
  const [planOpen, setPlanOpen] = useState(false);
  const [secureOpen, setSecureOpen] = useState(false);
  const [inquiry, setInquiry] = useState<MonitoringListItem | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const items = q.data?.items ?? [];
  const s = q.data?.settings;
  const open = items.filter((m) => m.status === 'planned' || m.status === 'in_progress');
  const nextVisit = open.filter((m) => m.kind !== 'inquiry').sort((a, b) => a.due_year - b.due_year)[0] ?? null;
  const nextInquiry = open.filter((m) => m.kind === 'inquiry').sort((a, b) => a.due_year - b.due_year)[0] ?? null;
  useEffect(() => {
    const id = Number(sp.get('nachfrage'));
    if (id && items.length) {
      const m = items.find((x) => x.id === id);
      if (m) setInquiry(m);
      const n = new URLSearchParams(sp);
      n.delete('nachfrage');
      setSp(n, { replace: true });
    }
  }, [sp, items, setSp]);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['area-monitorings', area.id] });
    qc.invalidateQueries({ queryKey: ['area', area.id] });
    qc.invalidateQueries({ queryKey: ['areas'] });
    qc.invalidateQueries({ queryKey: ['monitorings'] });
    qc.invalidateQueries({ queryKey: ['today'] });
  };
  const secure = useMutation({
    mutationFn: () => api.post<{ applied: string[] }>(`/areas/${area.id}/secure`, {}),
    onSuccess: (r) => {
      toast(r.applied.join(' · '));
      setSecureOpen(false);
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const startNow = useMutation({
    mutationFn: async () => {
      if (nextVisit) return nextVisit.id;
      const m = await api.post<MonitoringDetail>(`/areas/${area.id}/monitorings`, { kind: 'additional', due_year: new Date().getFullYear() });
      return m.id;
    },
    onSuccess: (id) => nav(`/monitoring/${id}`),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  if (!can('monitoring')) return null;
  const intervals = s?.intervals ?? [2, 5];
  const win = windowLabel(s?.window);
  const secured = area.status === 'gesichert' || Boolean(area.secured_at);
  const hasPlanting = Boolean(area.planting_year);
  return (
    <Panel title="Monitoring" action={<span className="mut small">Regel: nach {intervals.join(' und ')} Jahren, {win}</span>}>
      {q.isLoading ? (
        <Skeleton rows={3} />
      ) : items.length === 0 ? (
        <div className="empty">{hasPlanting ? 'Noch kein Monitoring geplant. Der Abschluss einer Pflanzaktion legt die Termine an.' : 'Monitoring beginnt mit dem Abschluss der Pflanzaktion.'}</div>
      ) : (
        <div className="tw">
          <table className="mon-hist">
            <thead>
              <tr>
                <th>Monitoring</th>
                <th>Stand</th>
                <th>Datum / durch</th>
                <th>Zustand</th>
                <th>Nachbesserung</th>
                <th className="num">Fotos</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <MonitoringRow key={m.id} m={m} expanded={expanded === m.id} onToggle={() => setExpanded(expanded === m.id ? null : m.id)} write={write} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(write || secured || items.length > 0) && (
        <div className="pb mon-foot">
          <div>
            {secured ? (
              <strong className="ok">Kultur gesichert am {fmtDate(area.secured_at)}</strong>
            ) : nextVisit ? (
              <>
                <strong>Nächstes Monitoring fällig: {nextVisit.due_year}</strong> <span className="mut">({win})</span>
                {nextVisit.due.kind === 'overdue' && <span className="miss"> · überfällig seit {fmtDate(nextVisit.due.window_end)}</span>}
                {nextInquiry && nextInquiry.due_year < nextVisit.due_year && <span className="mut"> · Nachfrage {nextInquiry.due_year}</span>}
              </>
            ) : nextInquiry ? (
              <>
                <strong>Nachfrage beim Förster {nextInquiry.due_year}</strong> <span className="mut">· kein weiteres Regelmonitoring</span>
              </>
            ) : items.length > 0 ? (
              <span className="mut">Kein Monitoring geplant</span>
            ) : null}
          </div>
          {write && (
            <div className="actions">
              {!secured && hasPlanting && (
                <button type="button" className="btn primary sm" onClick={() => startNow.mutate()} disabled={startNow.isPending}>
                  <Icon name="edit" /> Monitoring erfassen
                </button>
              )}
              {hasPlanting && (
                <button type="button" className="btn sm" onClick={() => setPlanOpen(true)}>
                  Zusätzliches Monitoring planen
                </button>
              )}
              {!secured && area.forester && (
                <button type="button" className="btn sm" onClick={() => setInquiry(nextInquiry ?? ({ id: 0, due_year: new Date().getFullYear() } as MonitoringListItem))}>
                  <Icon name="mail" /> Nachfrage an Förster
                </button>
              )}
              {!secured && hasPlanting && (
                <button type="button" className="btn sm" onClick={() => setSecureOpen(true)}>
                  Als gesichert markieren
                </button>
              )}
              <a className="btn sm" href={`/api/v1/areas/${area.id}/monitoring-print`} target="_blank" rel="noreferrer">
                <Icon name="doc" /> Monitoring-Doku
              </a>
            </div>
          )}
        </div>
      )}
      {planOpen && <PlanModal areaId={area.id} onClose={() => setPlanOpen(false)} onDone={() => (setPlanOpen(false), invalidate())} />}
      {secureOpen && <ConfirmDialog title="Kultur als gesichert markieren" text={<>„{area.name}“ wird auf „Gesichert“ gesetzt. {open.length > 0 ? `${open.length} offene Monitoring-Termine und Nachfragen entfallen mit dem Grund „Kultur gesichert“.` : ''}</>} confirmLabel="Als gesichert markieren" onConfirm={() => secure.mutate()} onClose={() => setSecureOpen(false)} busy={secure.isPending} />}
      {inquiry && <InquiryModal area={area} m={inquiry} senderName={me?.user.display_name ?? null} onClose={() => setInquiry(null)} onDone={() => (setInquiry(null), invalidate())} />}
    </Panel>
  );
}

function MonitoringRow({ m, expanded, onToggle, write }: { m: MonitoringListItem; expanded: boolean; onToggle: () => void; write: boolean }) {
  const detail = useQuery({ queryKey: ['monitoring', m.id], queryFn: () => api.get<MonitoringDetail>(`/monitorings/${m.id}`), enabled: expanded });
  const open = m.status === 'planned' || m.status === 'in_progress';
  const d = detail.data;
  return (
    <>
      <tr className={`row ${expanded ? 'open' : ''}`} onClick={onToggle} aria-expanded={expanded}>
        <td style={{ whiteSpace: 'nowrap' }}>{monitoringTitle(m)}</td>
        <td>
          <DuePill due={m.due} status={m.status} />
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {m.done_at ? fmtDate(m.done_at) : m.assigned ? <span className="mut">zuständig {m.assigned.initials ?? m.assigned.display_name}</span> : '–'}
          {m.done_by && <span className="sm">{m.done_by}</span>}
        </td>
        <td>{conditionLabel(m.condition)}</td>
        <td>{d ? improvementLabel(d.improvement_needed) : m.status === 'done' ? <span className="mut">aufklappen</span> : '–'}</td>
        <td className="num">{m.photo_count || ''}</td>
        <td className="num">
          <Link className={`btn sm ${open && write ? 'primary' : ''}`} to={`/monitoring/${m.id}`} onClick={(e) => e.stopPropagation()}>
            {open && write ? (m.kind === 'inquiry' ? 'Nachfrage' : 'Erfassen') : 'Ansehen'}
          </Link>
        </td>
      </tr>
      {expanded && (
        <tr className="detail">
          <td colSpan={7}>
            {detail.isLoading || !d ? (
              <Skeleton rows={2} />
            ) : (
              <dl className="dl">
                {d.kind !== 'inquiry' && (
                  <>
                    <dt>Wildschutz</dt>
                    <dd>
                      {wildlifeLabel(d.wildlife_intact)}
                      {d.wildlife_note ? ` · ${d.wildlife_note}` : ''}
                    </dd>
                    <dt>Anwuchs</dt>
                    <dd>
                      {[d.failure_rate_pct != null ? `Ausfall ${d.failure_rate_pct} %` : null, d.species_failures.length ? d.species_failures.map((f) => `${f.label}${f.failure_rate_pct != null ? ` ${f.failure_rate_pct} %` : ''}`).join(', ') : null, d.growth_success].filter(Boolean).join(' · ') || '–'}
                    </dd>
                    <dt>Wuchsverhalten</dt>
                    <dd>{d.growth_behavior ? GROWTH_BEHAVIOR_LABELS[d.growth_behavior] : '–'}</dd>
                    <dt>Nachbesserung</dt>
                    <dd>
                      {improvementLabel(d.improvement_needed)}
                      {d.improvement_note ? ` · ${d.improvement_note}` : ''}
                    </dd>
                    <dt>Weiteres Vorgehen</dt>
                    <dd>{d.next_steps || '–'}</dd>
                  </>
                )}
                <dt>Vereinbarungen</dt>
                <dd>{d.agreements || '–'}</dd>
                <dt>Vermerke</dt>
                <dd>{d.remarks || '–'}</dd>
                {d.skip_reason && (
                  <>
                    <dt>Entfallen</dt>
                    <dd>{d.skip_reason}</dd>
                  </>
                )}
                {d.origin_action && (
                  <>
                    <dt>Pflanzaktion</dt>
                    <dd>
                      <Link className="link" to={`/aktionen/${d.origin_action.id}`}>
                        {d.origin_action.code}
                      </Link>
                    </dd>
                  </>
                )}
                {d.photos.length > 0 && (
                  <>
                    <dt>Fotos</dt>
                    <dd>
                      <div className="photo-grid">
                        {d.photos.slice(0, 6).map((p) => (
                          <a key={p.id} href={`/api/v1/documents/${p.id}/file`} target="_blank" rel="noreferrer">
                            <img src={p.has_thumb ? `/api/v1/documents/${p.id}/thumb` : `/api/v1/documents/${p.id}/file`} alt={p.original_name} />
                          </a>
                        ))}
                      </div>
                    </dd>
                  </>
                )}
              </dl>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Zusätzliches Monitoring oder Nachfrage planen (M-02) */
function PlanModal({ areaId, onClose, onDone }: { areaId: number; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const users = useUsersPick();
  const year = new Date().getFullYear();
  const [kind, setKind] = useState<'additional' | 'inquiry'>('additional');
  const [dueYear, setDueYear] = useState(String(year + 1));
  const [user, setUser] = useState('');
  const [remarks, setRemarks] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<MonitoringDetail>(`/areas/${areaId}/monitorings`, { kind, due_year: Number(dueYear), assigned_user_id: user ? Number(user) : null, remarks: remarks || null }),
    onSuccess: (r) => {
      toast(`${monitoringTitle(r)} ${r.due_year} geplant`);
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title="Monitoring planen"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" disabled={m.isPending} onClick={() => m.mutate()}>
            Planen
          </button>
        </>
      }
    >
      <div className="form one">
        <Select label="Art" value={kind} onChange={(v) => setKind(v as 'additional' | 'inquiry')} options={[{ value: 'additional', label: 'Zusätzliches Monitoring vor Ort' }, { value: 'inquiry', label: 'Nachfrage beim Förster (E-Mail, ohne Vor-Ort-Termin)' }]} placeholder="wählen" />
        <Select label="Fälligkeitsjahr" value={dueYear} onChange={setDueYear} options={YEAR_OPTIONS(year + 1).map((y) => ({ value: String(y), label: String(y) }))} placeholder="wählen" />
        <Select label="Zuständig" value={user} onChange={setUser} options={(users.data ?? []).map((u) => ({ value: String(u.id), label: `${u.initials ? `${u.initials} · ` : ''}${u.display_name}` }))} placeholder="– niemand –" hint="Setzt den Status auf „in Bearbeitung“" />
        <TextArea label="Grund / Vermerk" value={remarks} onChange={setRemarks} rows={2} placeholder="z. B. hohe Ausfälle nach Dürresommer" />
      </div>
    </Modal>
  );
}

/** Nachfrage an den Förster (M-06, Plan 06): E-Mail aus dem System mit Vorlage „Monitoring-Nachfrage“, Versand mit Outlook-Kopie; danach als erledigt vermerken */
function InquiryModal({ area, m, senderName, onClose, onDone }: { area: AreaDetail; m: MonitoringListItem; senderName: string | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [doneAt, setDoneAt] = useState(todayIso());
  const [note, setNote] = useState('');
  const [mid, setMid] = useState<number>(m.id);
  const [mailId, setMailId] = useState<number | null>(null);
  const ensure = async () => {
    if (mid) return mid;
    const c = await api.post<MonitoringDetail>(`/areas/${area.id}/monitorings`, { kind: 'inquiry', due_year: m.due_year });
    setMid(c.id);
    qc.invalidateQueries({ queryKey: ['area-monitorings', area.id] });
    return c.id;
  };
  const prepare = useMutation({
    mutationFn: async () => api.post<EmailDto & { notes: string[] }>(`/monitorings/${await ensure()}/emails/prepare`, {}),
    onSuccess: (e) => {
      for (const n of e.notes ?? []) toast(n, 'error');
      setMailId(e.id);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const complete = useMutation({
    mutationFn: async () => api.post(`/monitorings/${await ensure()}/complete`, { done_at: doneAt, done_by: senderName, agreements: note || null }),
    onSuccess: () => {
      toast('Nachfrage als erledigt vermerkt');
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title={`Nachfrage beim Förster ${m.due_year}`}
      subtitle={area.name}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Schließen
          </button>
          <button className="btn primary" type="button" disabled={complete.isPending} onClick={() => complete.mutate()}>
            Als erledigt vermerken
          </button>
        </>
      }
    >
      <div className="form one">
        {area.forester ? (
          <button type="button" className="btn" style={{ alignSelf: 'flex-start' }} onClick={() => prepare.mutate()} disabled={prepare.isPending}>
            <Icon name="mail" /> E-Mail an {area.forester.display_name} prüfen und senden
          </button>
        ) : (
          <span className="mut">Der Fläche ist kein Förster zugeordnet.</span>
        )}
        {mailId != null && mid ? <EmailModal emailId={mailId} entityType="monitoring" entityId={mid} onClose={() => setMailId(null)} onSent={() => qc.invalidateQueries({ queryKey: ['area-monitorings', area.id] })} /> : null}
        <DateInput label="Nachgefragt am" value={doneAt} onChange={setDoneAt} />
        <TextArea label="Rückmeldung / Vereinbarungen" value={note} onChange={setNote} rows={3} placeholder="kann auch später am Vorgang ergänzt werden" />
      </div>
    </Modal>
  );
}
